/**
 * Route table primitives, error envelope and cursor pagination shared by every API resource file.
 */
import { z } from 'zod';
import type { Capability } from '../roles.js';

export const ROUTE_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type RouteMethod = (typeof ROUTE_METHODS)[number];

/**
 * desktop: bearer session issued by the desktop exchange; admin: HttpOnly cookie session with CSRF;
 * public: no session (health, JWKS, login redirects, exchange).
 */
export const ROUTE_AUTHS = ['desktop', 'admin', 'public'] as const;
export type RouteAuth = (typeof ROUTE_AUTHS)[number];

export interface RouteDef<
  Id extends string = string,
  Req extends z.ZodType = z.ZodType,
  Res extends z.ZodType = z.ZodType,
  Params extends z.ZodType = z.ZodType,
> {
  id: Id;
  method: RouteMethod;
  /** Fastify-style path with `:param` segments. */
  path: string;
  auth: RouteAuth;
  /** Required capability (any-of when an array). Absent = authenticated session is enough. */
  capability?: Capability | readonly Capability[];
  /** Sensitive admin change: session.recentAuthAt must be within RECENT_AUTH_WINDOW_MS. */
  recentAuth?: boolean;
  /** Path params schema (absent when the path has no params). */
  paramsSchema?: Params;
  /** Query (GET/DELETE) or body (POST/PUT/PATCH). */
  requestSchema: Req;
  responseSchema: Res;
  /** Disable request/response body logging (secret-bearing routes). */
  noBodyLog?: boolean;
  /** Routes that must not apply the default rate-limit bucket. */
  rateLimit?: { max: number; windowSeconds: number };
  description: string;
}

/** Identity helper that preserves literal types for the ROUTES table. */
export function defineRoute<
  const Id extends string,
  Req extends z.ZodType,
  Res extends z.ZodType,
  Params extends z.ZodType = z.ZodNever,
>(def: RouteDef<Id, Req, Res, Params>): RouteDef<Id, Req, Res, Params> {
  return def;
}

// added by server-admin: admin mutations return the id of the audit event they wrote in this response header
// (the JSON body stays the entity contract). CORS exposes it to the admin origin.
export const AUDIT_EVENT_ID_HEADER = 'x-audit-event-id';

/** Sensitive admin changes require authentication within this window (BUILD_PLAN section 2.2). */
export const RECENT_AUTH_WINDOW_MS = 10 * 60 * 1000;

export const IsoDateTimeSchema = z.iso.datetime();
export type IsoDateTime = z.infer<typeof IsoDateTimeSchema>;

export const SemVerSchema = z
  .string()
  .regex(/^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?(\+[0-9A-Za-z.-]+)?$/, 'expected semver');
export type SemVer = z.infer<typeof SemVerSchema>;

/** Semver range as used for compatibleAppVersions (e.g. ">=1.0.0 <2.0.0"). */
export const SemVerRangeSchema = z.string().min(1).max(120);

export const Sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/, 'expected sha256 hex');
export type Sha256Hex = z.infer<typeof Sha256HexSchema>;

/** Compact JWS (three base64url segments). */
export const CompactJwsSchema = z.string().regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+$/, 'expected compact JWS');
export type CompactJws = z.infer<typeof CompactJwsSchema>;

export const Ed25519JwkSchema = z.object({
  kty: z.literal('OKP'),
  crv: z.literal('Ed25519'),
  x: z.string().min(1),
  kid: z.string().min(1).optional(),
  alg: z.literal('EdDSA').optional(),
  use: z.literal('sig').optional(),
});
export type Ed25519Jwk = z.infer<typeof Ed25519JwkSchema>;

export const JwksSchema = z.object({ keys: z.array(Ed25519JwkSchema) });
export type Jwks = z.infer<typeof JwksSchema>;

export const MicroUsdSchema = z.number().int().nonnegative();
export const CountSchema = z.number().int().nonnegative();
export const RevisionSchema = z.number().int().nonnegative();

/**
 * Query-string-safe variants for GET/DELETE routes, where the admin client and the server mount
 * carry the request as `?key=value` and every value arrives as a string. Plain `z.number()` /
 * `z.boolean()` reject those, so a DELETE with `revision` or a boolean flag can never validate.
 */
export const QueryRevisionSchema = z.union([RevisionSchema, z.string().regex(/^\d+$/).transform((s) => Number(s))]).pipe(RevisionSchema);
export const QueryBooleanSchema = z.union([z.boolean(), z.enum(['true', 'false', '1', '0']).transform((s) => s === 'true' || s === '1')]);

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

export const API_ERROR_CODES = [
  'bad_request',
  'validation_failed',
  'unauthorized',
  'forbidden',
  'recent_auth_required',
  'not_found',
  'conflict',
  'revision_mismatch',
  'rate_limited',
  'user_suspended',
  'user_not_invited',
  'device_revoked',
  'device_unknown',
  'entitlement_denied',
  'grant_already_redeemed',
  'grant_expired',
  'grant_denied',
  'budget_exceeded',
  'no_active_assignment',
  'lease_held',
  'signature_invalid',
  'sequence_conflict',
  'payload_too_large',
  'unsupported_version',
  // real-google change: `integrations.google.client` before an administrator stored the desktop client.
  'integration_not_configured',
  'internal',
] as const;
export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES);
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>;

export const API_ERROR_STATUS: Record<ApiErrorCode, number> = {
  bad_request: 400,
  validation_failed: 400,
  unauthorized: 401,
  forbidden: 403,
  recent_auth_required: 403,
  not_found: 404,
  conflict: 409,
  revision_mismatch: 409,
  rate_limited: 429,
  user_suspended: 403,
  user_not_invited: 403,
  device_revoked: 403,
  device_unknown: 403,
  entitlement_denied: 403,
  grant_already_redeemed: 409,
  grant_expired: 410,
  grant_denied: 403,
  budget_exceeded: 402,
  no_active_assignment: 409,
  lease_held: 409,
  signature_invalid: 400,
  sequence_conflict: 409,
  payload_too_large: 413,
  unsupported_version: 426,
  // real-google change
  integration_not_configured: 409,
  internal: 500,
};

export const ApiErrorSchema = z.object({
  code: ApiErrorCodeSchema,
  message: z.string(),
  requestId: z.string(),
  /** Structured, allowlisted detail; never provider bodies. */
  details: z.record(z.string(), z.unknown()).optional(),
  /** Seconds, when code = rate_limited or lease_held. */
  retryAfterSeconds: z.number().int().nonnegative().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

// ---------------------------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------------------------

export const DEFAULT_PAGE_LIMIT = 50;
export const MAX_PAGE_LIMIT = 200;

export const CursorQuerySchema = z.object({
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT).default(DEFAULT_PAGE_LIMIT),
});
export type CursorQuery = z.infer<typeof CursorQuerySchema>;

export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    /** Total when cheap to compute; null otherwise. */
    total: z.number().int().nonnegative().nullable().optional(),
  });
}
export type Page<T> = { items: T[]; nextCursor: string | null; total?: number | null };

export const OkSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof OkSchema>;

export const EmptyRequestSchema = z.object({}).strict();
/** GET routes without query parameters. */
export const NoQuerySchema = z.object({}).loose();

/** Response schema for routes that answer with an HTTP redirect (admin BFF login/callback). */
export const RedirectResponseSchema = z.object({ redirectTo: z.string() });

export const TimeRangeQuerySchema = z.object({
  from: IsoDateTimeSchema.optional(),
  to: IsoDateTimeSchema.optional(),
});

export const EXPORT_FORMATS = ['csv', 'json'] as const;
export const ExportFormatSchema = z.enum(EXPORT_FORMATS);
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

export const ExportResultSchema = z.object({
  exportId: z.string(),
  format: ExportFormatSchema,
  generatedAt: IsoDateTimeSchema,
  timezone: z.string(),
  rowCount: CountSchema,
  /** Server-relative download path, valid for a bounded time. */
  downloadPath: z.string(),
  expiresAt: IsoDateTimeSchema,
  filters: z.record(z.string(), z.unknown()),
});
export type ExportResult = z.infer<typeof ExportResultSchema>;
